import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Card } from '@safetyhub/ui';
import { formatDateToLocal } from '@safetyhub/shared';
import {
  getLegalDocumentPastSites,
  legalDocumentsPastQueryKeys,
  legalPastSitePath,
} from '../../lib/legalDocumentsPast';
import { ChevronRightIcon } from '../icons';

/**
 * 법정 서류 첫 화면의 「계약이 끝난 현장」(L32 · 읽기 전용) — 대행 계약이 끝나 기존 현장 목록에서 빠진 현장을
 * 따로 보인다. 지난 기록 현장이 **없으면 아무것도 그리지 않는다**(대부분의 조직은 이 칸이 영영 비어 있다 —
 * 빈 카드를 그리면 기능이 없는 것처럼 읽힌다).
 * 조회 실패도 그리지 않는다 — 이 칸은 본 목록의 보조이고, 실패를 크게 그리면 본 목록이 깨진 것처럼 보인다.
 */
export default function PastSitesPanel({ companyId }: { companyId: string }) {
  const { data } = useQuery({
    queryKey: legalDocumentsPastQueryKeys.sites(companyId),
    queryFn: () => getLegalDocumentPastSites(companyId),
  });
  const sites = data?.sites ?? [];
  if (sites.length === 0) return null;

  return (
    <section className="mt-6" aria-label="계약이 끝난 현장">
      <h2 className="mb-2 text-sm font-bold text-ink-700">계약이 끝난 현장</h2>
      <p className="mb-3 text-xs text-ink-500">대행 계약이 끝나기 전까지의 기록을 읽기 전용으로 봅니다.</p>
      <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
        <ul className="divide-y divide-ink-200">
          {sites.map((site) => (
            <li key={site.siteId}>
              <Link
                to={legalPastSitePath(site.siteId)}
                className="flex items-center gap-3 px-4 py-4 hover:bg-surface-hover sm:px-6"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-ink-900">{site.siteName}</p>
                  <p className="mt-0.5 truncate text-xs text-ink-500">
                    대행 기관 {site.agencyName} · 계약 종료 {formatDateToLocal(new Date(site.accessUntil))}
                  </p>
                </div>
                <Badge tone="slate" size="sm">
                  읽기 전용
                </Badge>
                <ChevronRightIcon className="h-4 w-4 flex-none text-ink-400" />
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
